import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { useGetGeoStatsQuery } from '@shared/api';
import { IconArrowLeft } from '@shared/ui/icons';
import { riseIn } from '@shared/theme/animations';
import { PtoProjectNav } from './PtoProjectNav';

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(7)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 820px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Tabs = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(5)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
`;

const Tab = styled(Link)<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-radius: ${({ theme }) => `${theme.radius.sm} ${theme.radius.sm} 0 0`};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &::after {
    content: '';
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    right: ${({ theme }) => theme.spacing(3)};
    bottom: -1px;
    height: 2px;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }
`;

const Count = styled.span`
  min-width: 22px;
  padding: 1px 7px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: center;
  background: ${({ theme }) => theme.colors.status.warning}29;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * Оболочка агента ОСНОВА.Инженер (ИИ-агент ПТО). На корне `/agents/pto` и в
 * реестре исполнительных схем — заголовок и вкладки «Проекты и РД» /
 * «Исполнительные схемы» (бейдж — сколько ждут проверки); на вложенных путях —
 * кнопка «назад».
 */
export function PtoLayout() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean); // ['agents','pto',...]
  const isRoot = segments.length === 2; // /agents/pto
  const inExec = segments[2] === 'exec-schemes';
  const isExecList = inExec && segments.length === 3;
  const topSegment = segments[2] ?? '';
  const activeProjectId =
    topSegment && !['exec-schemes', 'rd-import', 'equipment'].includes(topSegment)
      ? topSegment
      : '';
  const { can_read, can_write } = useAgentPermission('pto');
  const { data: geoStats } = useGetGeoStatsQuery(undefined, {
    skip: !can_read,
    pollingInterval: 60_000,
  });
  const awaiting = geoStats?.awaiting_review ?? 0;

  return (
    <Wrap>
      <Header>
        {isRoot || isExecList ? (
          <>
            <Title>
              ОСНОВА<span className="accent">.Инженер</span>
            </Title>
            <Subtitle>
              Производственно-технический отдел: реестр рабочей документации (РД) с версионностью и
              вопросно-ответный режим по РД с обязательными ссылками на шифр и лист. Числа берутся
              только из документации — модель ничего не выдумывает.
            </Subtitle>
            <Tabs aria-label="Разделы ОСНОВА.Инженер">
              <Tab to="/agents/pto" $active={isRoot} aria-current={isRoot ? 'page' : undefined}>
                Проекты и РД
              </Tab>
              <Tab
                to="/agents/pto/exec-schemes"
                $active={inExec}
                aria-current={isExecList ? 'page' : undefined}
              >
                Исполнительные схемы
                {awaiting > 0 && (
                  <Count aria-label={`ждут проверки: ${awaiting}`}>{awaiting}</Count>
                )}
              </Tab>
            </Tabs>
          </>
        ) : inExec ? (
          <Back to="/agents/pto/exec-schemes">
            <IconArrowLeft size={16} />
            <span>ОСНОВА.Инженер · Исполнительные схемы</span>
          </Back>
        ) : (
          <Back to="/agents/pto">
            <IconArrowLeft size={16} />
            <span>ОСНОВА.Инженер · Все проекты</span>
          </Back>
        )}
      </Header>
      {activeProjectId && <PtoProjectNav projectId={activeProjectId} />}
      {can_read && !can_write && <ReadOnlyBanner />}
      <Outlet />
    </Wrap>
  );
}
